import Component from '@glimmer/component'
import PhosphorIcon from 'teacher-ui-client/components/phosphor-icon'
import { tracked } from '@glimmer/tracking'
import { service } from '@ember/service'
import { action } from '@ember/object'
import { didCancel, type Task } from 'ember-concurrency'
import { isConflictError } from 'teacher-ui-client/resource-conflict/error'
import type { IntlService } from 'ember-intl'
import { setIntlLanguage } from 'teacher-ui-client/utils/locales'
import { Changeset } from 'ember-changeset'
import lookupValidator from 'teacher-ui-client/utils/lookup-validator'
import { editTeacherValidator, editTeacherSelfValidator } from 'teacher-ui-client/teacher/validator'
import type { BufferedChangeset } from 'ember-changeset/types'
import type FlashQueueService from 'teacher-ui-client/services/flash-queue'
import type ResourceConflictService from 'teacher-ui-client/services/resource-conflict'
import type ErrorHandlerService from 'teacher-ui-client/services/error-handler'
import type ErrorHandler from 'teacher-ui-client/utils/error-handler'
import type SessionService from 'teacher-ui-client/services/session'
import type Teacher from 'teacher-ui-client/models/teacher'
import { FormField } from 'teacher-ui-client/components/form-field'
import type { SubscriptionType } from 'teacher-ui-client/subscription-type/subscription-types'
import { on } from '@ember/modifier'
import { t } from 'ember-intl'
import { themeKey } from 'teacher-ui-client/helpers/theme-key'
import { pick } from 'teacher-ui-client/helpers/pick'
import { BaseSelect } from 'teacher-ui-client/components/base/select'
import { UiButton } from 'teacher-ui-client/components/ui-button'
import { FormWrapperSubmitButton } from 'teacher-ui-client/components/form-wrapper/submit-button'
import { eq, or } from 'ember-truth-helpers'
import { ButtonAppearance } from 'teacher-ui-client/components/ui-button/themes'
import submitButtonState from 'teacher-ui-client/helpers/submit-button-state'
import type Owner from '@ember/owner'

interface Args {
  teacher: Teacher
  saveTeacherTask: Task<void, [BufferedChangeset, object]>
  subscriptionType: SubscriptionType
  usesExternalProvisioning?: boolean
  usesExternalAuthentication?: boolean
  isOneRosterEnabled?: boolean
}

interface Signature {
  Args: Args
  Element: HTMLFormElement
}

const UNSELECTED_VALUE = ''

export class FormEditTeacher extends Component<Signature> {
  @service flashQueue!: FlashQueueService

  @service intl!: IntlService

  @service resourceConflict!: ResourceConflictService

  @service('error-handler') errorHandlerService!: ErrorHandlerService

  @service session!: SessionService

  @tracked errorHandler!: ErrorHandler

  @tracked teacherChangeset!: BufferedChangeset

  constructor(owner: Owner, args: Args) {
    super(owner, args)

    const validator = this.isCurrentTeacher ? editTeacherSelfValidator : editTeacherValidator

    this.teacherChangeset = Changeset(this.args.teacher, lookupValidator(validator, { intl: this.intl }), validator)
    void this.teacherChangeset.validate()

    this.errorHandler = this.errorHandlerService.create()

    this.errorHandler.registerErrorDefinition({
      statusCode: '422',
      serverMessage: 'login has already been taken',
      changesetProperty: 'login',
      changeset: this.teacherChangeset,
      kind: 'changeset',
      translationKey: 'validation.message.teacher.loginTaken',
    })

    this.errorHandler.registerErrorDefinition({
      statusCode: '422',
      serverMessage: 'email has already been taken',
      changesetProperty: 'email',
      changeset: this.teacherChangeset,
      kind: 'changeset',
      translationKey: 'validation.message.teacher.emailTaken',
    })

    this.errorHandler.registerErrorDefinition({
      statusCode: '422',
      serverMessage: 'password current does not match existing password.',
      changesetProperty: 'passwordCurrent',
      changeset: this.teacherChangeset,
      kind: 'changeset',
      translationKey: 'validation.message.teacher.passwordIncorrect',
    })
  }

  get isCurrentTeacher() {
    return this.args.teacher.id === this.session.currentTeacher.id
  }

  /* Give them a choice of base languages. These should be derived from ISO 639-1 codes
   * https://en.wikipedia.org/wiki/List_of_ISO_639-1_codes
   *
   * The extra data like which variation on the base language (like en-gb), will be handled in the backend based on the
   * account, and returned as a language tag in the language attribute that TUI then consumes.
   */
  get localeOptions() {
    return [
      // Intentionally don't translate these options
      // Korean/Chinese labels are using unicode escaped sequences as the raw unicode characters break Prettier.
      { value: 'en', label: 'English' },
      { value: 'ko', label: 'Korean - 한국인' },
      { value: 'zh', label: 'Chinese - 中国人' },
      { value: UNSELECTED_VALUE, label: this.intl.t('manage.persons.edit.localeOptions.placeholder') },
    ]
  }

  // convert nullish to the unselected/default value. This is to aid with the html select
  get locale(): string {
    return this.teacherChangeset.get('locale') ?? UNSELECTED_VALUE
  }

  set locale(value: string) {
    const { teacherChangeset } = this
    // if chosen value is the unselected/default option, set as null
    const valueToSet = value === UNSELECTED_VALUE ? null : value
    // if the newly selected value is the same as original value, just rollback
    if (teacherChangeset.get('data.locale') === valueToSet) {
      teacherChangeset.rollbackProperty('locale')
    } else {
      teacherChangeset.set('locale', valueToSet)
    }
  }

  @action
  private rollback() {
    if (this.teacherChangeset.isDirty && this.args.saveTeacherTask.isIdle) {
      this.teacherChangeset.rollback()
    }
  }

  @action
  onReset(event: Event) {
    event.preventDefault()
    this.rollback()
  }

  @action
  async onSubmit(event: Event) {
    event.preventDefault()

    const { errorHandler, teacherChangeset } = this
    try {
      const teacher = teacherChangeset.get('data')
      const beforeLanguage = teacher.language
      const passwordCurrent = teacherChangeset.get('passwordCurrent')
      const password = teacherChangeset.get('password')
      const passwordConfirmation = teacherChangeset.get('passwordConfirmation')

      // Clear the password inputs shown before saving
      teacherChangeset.rollbackProperty('passwordCurrent')
      teacherChangeset.rollbackProperty('password')
      teacherChangeset.rollbackProperty('passwordConfirmation')

      await this.args.saveTeacherTask.perform(teacherChangeset, {
        adapterOptions: { passwordCurrent, password, passwordConfirmation },
      })

      // Updating the teacher.locale attr may result in a new teacher.language being returned
      const languageChanged = beforeLanguage !== teacher.language

      // Update language
      if (languageChanged && this.isCurrentTeacher) {
        await setIntlLanguage(this.intl, teacher.language)
      }

      const name = teacherChangeset.get('fullName')
      const updateMessage = this.intl.t('manage.manageTeachers.edit.success', { name })
      this.flashQueue.addSuccess({ subtitle: updateMessage })
    } catch (e: any) {
      if (!didCancel(e)) {
        const errors = e?.errors || []
        errorHandler.handle(errors)
        errorHandler.sendOtherErrorsToLog('Error while updating teacher from the "edit teacher" component')

        const teacher = teacherChangeset.get('data')
        teacher.rollbackAttributes()

        if (isConflictError(e)) {
          return this.resourceConflict.handleError('update teacher', e)
        }
        const name = teacherChangeset.get('fullName')
        const message = this.intl.t('manage.manageTeachers.edit.error', { name })

        if (errorHandler.hasUnhandledErrors()) this.flashQueue.addFail({ subtitle: message })
      }
    }
  }

  @action
  setLocale(value: string) {
    this.locale = value
  }

  <template>
    <form
      data-test-edit-teacher
      class="space-y-3"
      autocomplete="off"
      {{on "submit" this.onSubmit}}
      {{on "reset" this.onReset}}
      ...attributes
    >
      <div class="font-semibold">
        {{#if this.isCurrentTeacher}}
          {{t "manage.persons.edit.myDetails"}}
        {{else}}
          {{t "manage.persons.edit.editDetails"}}
        {{/if}}
      </div>
      <div class="grid grid-cols-1 gap-x-6 gap-y-3 lg:grid-flow-col lg:grid-cols-3">
        {{! Details column }}
        <div class="space-y-3">
          <FormField
            @changeset={{this.teacherChangeset}}
            @propertyName="firstName"
            @labelText={{t "manage.persons.edit.firstName"}}
            as |field|
          >
            <field.label class="font-normal" />
            <field.input data-test-first-name disabled={{or @usesExternalProvisioning @isOneRosterEnabled}} />
          </FormField>
          <FormField
            @changeset={{this.teacherChangeset}}
            @propertyName="lastName"
            @labelText={{t "manage.persons.edit.lastName"}}
            as |field|
          >
            <field.label class="font-normal" />
            <field.input data-test-last-name disabled={{or @usesExternalProvisioning @isOneRosterEnabled}} />
          </FormField>
          <FormField
            @changeset={{this.teacherChangeset}}
            @propertyName="email"
            @labelText={{t "manage.persons.edit.email"}}
            as |field|
          >
            <field.label class="font-normal" />
            <field.input data-test-email type="email" disabled={{or @usesExternalProvisioning @isOneRosterEnabled}} />
          </FormField>
        </div>
        {{#unless (or @usesExternalAuthentication @isOneRosterEnabled)}}
          {{! Auth column (login & password) }}
          <div class="space-y-3">
            <FormField
              @changeset={{this.teacherChangeset}}
              @propertyName="login"
              @labelText={{t "manage.persons.edit.login"}}
              as |field|
            >
              <field.label class="font-normal" />
              <field.input data-test-login />
            </FormField>
            {{#if this.isCurrentTeacher}}
              <FormField
                @changeset={{this.teacherChangeset}}
                @propertyName="passwordCurrent"
                @labelText={{t "manage.persons.edit.passwordCurrent"}}
                as |field|
              >
                <field.label class="font-normal" />
                <field.input type="password" />
              </FormField>
            {{/if}}
            <FormField
              @changeset={{this.teacherChangeset}}
              @propertyName="password"
              @labelText={{t "manage.persons.edit.passwordNew"}}
              as |field|
            >
              <field.label class="font-normal" />
              <field.input type="password" />
            </FormField>

            <FormField
              @changeset={{this.teacherChangeset}}
              @propertyName="passwordConfirmation"
              @labelText={{t "manage.persons.edit.passwordConfirmNew"}}
              as |field|
            >
              <field.label class="font-normal" />
              <field.input type="password" />
            </FormField>
          </div>
        {{/unless}}
        {{#if this.isCurrentTeacher}}
          {{! Teacher settings column }}
          <div class="space-y-3">
            <FormField
              @changeset={{this.teacherChangeset}}
              @propertyName="locale"
              @labelText={{t "manage.persons.edit.locale"}}
              as |field|
            >
              <PhosphorIcon @icon="phosphor-globe-fill" />
              <field.label class="font-normal" />
              <BaseSelect
                id={{field.fieldId}}
                name="locale"
                class="w-full"
                @themeKey={{themeKey @subscriptionType}}
                value={{this.locale}}
                {{on "input" (pick "target.value" this.setLocale)}}
              >
                {{#each this.localeOptions as |opt|}}
                  <option value={{opt.value}} selected={{eq opt.value this.locale}}>{{opt.label}}</option>
                {{/each}}
              </BaseSelect>
            </FormField>
          </div>
        {{/if}}
      </div>

      <div class="text-center">
        <UiButton
          data-test-edit-teacher-reset
          disabled={{this.teacherChangeset.isPristine}}
          @appearance={{ButtonAppearance.muted}}
          type="reset"
        >
          {{t "cancel"}}
        </UiButton>
        <FormWrapperSubmitButton
          data-test-edit-teacher-submit
          @disabled={{or this.teacherChangeset.isPristine this.teacherChangeset.isInvalid}}
          @appearance={{ButtonAppearance.regular}}
          @defaultText={{t "saveChanges"}}
          @state={{submitButtonState @saveTeacherTask.last this.errorHandler}}
        />
      </div>
    </form>
  </template>
}

export default FormEditTeacher
